import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { Box, ClockOutline, HStack, Link, Text } from '@bonusly/design-system';
import { useCurrentCompany } from '@bonusly/who-am-i';

import { useTrialStatus } from '../../../hooks/use-trial-status';

const OFFICE_HOURS_URL = 'https://calendly.com/bonusly-expert-office-hours';

interface TrialCardProps {
	isExpanded: boolean;
}

export const TrialCard: FC<TrialCardProps> = ({ isExpanded }) => {
	const keyPrefix = 'trial_card';
	const { t } = useTranslation('navigation', { keyPrefix });
	const company = useCurrentCompany();
	const { track } = useAnalytics();

	// Trial timing + eligibility come from the shared hook so the card, the admin
	// trial banner, and the interstitial gate stay in lockstep.
	const { daysLeft, isPostExpiry, isInScope, canSeeWhatsNext } = useTrialStatus();

	// The card always links to /trial/whats-next, which the endpoint gates to
	// finance/global admins, so we only render it for those admins. `isInScope`
	// excludes superadmin-initialized companies and out-of-window lapsed trials.
	if (!isExpanded || !canSeeWhatsNext || !isInScope) return null;

	// `count` is only rendered on the in-trial path, where `daysLeft` is
	// non-negative; the post-expiry path shows the expired copy instead. A
	// negative `daysLeft` here means a lapse within the post-expiry window
	// (`isInScope` already drops out-of-window lapses), so we keep rendering.
	const count = daysLeft ?? 0;

	return (
		<Box bgColor="bg.surface" borderRadius="md" my={2} p={3} width="100%">
			<Text color="text.default" fontSize="sm" fontWeight="semibold">
				{isPostExpiry ? t('expired_title') : t('title', { plan: company.product_plan_name })}
			</Text>

			<HStack mt={1} spacing={1}>
				<ClockOutline color="text.default" flexShrink={0} size="xs" />
				<Text color="text.default" fontSize="sm">
					{isPostExpiry ? t('expired_subtitle') : t('days', { count })}
				</Text>
			</HStack>

			<Link
				as={RouterLink}
				color="text.interactive"
				display="block"
				fontSize="sm"
				fontWeight="medium"
				to="/trial/whats-next"
				mt={2}
				onClick={() => track(AnalyticsEvent.OnboardingTrialBanner.ManagePlanClicked)}
				_hover={{ textDecor: 'none' }}
			>
				{isPostExpiry ? t('choose_plan') : t('manage_plan')}
			</Link>

			<Box mt={2} pt={2} borderTop="1px solid" borderTopColor="border.default">
				<Text color="text.softer" fontSize="sm">
					{t('need_help')}{' '}
					<Link href={OFFICE_HOURS_URL} target="_blank" _hover={{ textDecor: 'none' }}>
						{t('talk_to_expert')}
					</Link>
				</Text>
			</Box>
		</Box>
	);
};
